import React, { useState, useEffect } from "react";
import { Row, Col } from "../../bootstrap/index.jsx";
import Errors from "../../common/Errors.jsx";
import LoadingIndicator from "../../common/loading_indicator.jsx";
import Help from "../../common/Help.jsx";
import QuantitativeIndicatorFormFields from "./QuantitativeIndicatorFormFields.jsx";
import QualitativeIndicatorFormFields from "./QualitativeIndicatorFormFields.jsx";
import update from "immutability-helper";
import Disaggregations from "./DisaggregationFormFields.jsx";
import AdditionalFields from "./AdditionalFormFields.jsx";
import ObjectiveNumberField from "../objective/ObjectiveNumberField.jsx";
import ToggleSwitch from "../../common/ToggleSwitch.jsx";
import AggregateFormFields from "./AggregateFormFields.jsx";

import {
  result_edit_indicator_path,
  result_indicator_form_options_path,
  result_create_indicator_path,
  result_update_indicator_path
} from '~/routes';

export const DATA_TYPE_RATIO = "ratio";

export const INDICATOR_TYPE = {
  quantitative: "quantitative",
  qualitative: "qualitative",
  aggregate: "aggregate"
};

const IndicatorForm = ({
  formRef,
  autoNumber,
  indicatorId,
  resultId,
  setResultId,
  milestonesEnabled,
  errors,
  parentOptions,
  resultFrameworkId,
  isTemplate,
  mayManageAggregateIndicators,
  mayDefinePartialDisaggregations
}) => {
  const [indicator, setIndicator] = useState({});
  const [formOptions, setFormOptions] = useState(null);

  const { data_type, subtype } = indicator;
  const isQualitative = subtype == INDICATOR_TYPE.qualitative;
  const isAggregate = subtype == INDICATOR_TYPE.aggregate;
  const lockCommonIndicatorFields = !isTemplate && !!indicator.common_indicator_id;
  const hasLinks = indicator.link_count > 0;

  const fetchIndicator = () => {
    if (indicatorId) {
      const url = result_edit_indicator_path({
        indicator_id: indicatorId,
      });

      jQuery.get(url, (data) => {
        setIndicator(data);
      });
    } else {
      setIndicator({"disaggregation_type": 'strict'});
    }
  };

  useEffect(() => {
    fetchIndicator();
  }, [indicatorId]);

  const fetchFormOptions = () => {
    jQuery.get(
      result_indicator_form_options_path({
        result_framework_id: resultFrameworkId,
        indicator_id: indicatorId
      }),
      (data) => {
        setFormOptions(data);
      }
    );
  };

  useEffect(() => {
    fetchFormOptions();
  }, [resultFrameworkId, indicatorId]);

  useEffect(() => {
    if (!data_type && formOptions) {
      updateIndicator("data_type", formOptions.data_types[0].value);
    }
  }, [formOptions]);

  const updateIndicator = (attr, value) =>
    setIndicator(
      update(indicator, {
        $merge: { [attr]: value },
      })
    );


  const hiddenCommonIndicatorInput = <input type="hidden" name="is_common_indicator" value={indicator.is_common_indicator ? 1 : 0} />

  return (indicatorId && !indicator.id) || !formOptions ? (
    <LoadingIndicator />
  ) : (
    <form ref={formRef} className="indicator-form" action={indicator.id ? result_update_indicator_path({ indicator_id: indicator.id }) : result_create_indicator_path({ result_id: resultId, result_framework_id: resultFrameworkId })}>
      <Errors errors={errors} />
      <input type="hidden" name="lock_common_fields" value={lockCommonIndicatorFields} />
      <input type="hidden" name="lock_disaggregations" value={hasLinks} />
      {!isQualitative && isTemplate && (!indicatorId || indicator.is_common_indicator) ? (
        <>
          <Row className="form-group">
            <label className="col-xs-12">
              {I18n.t('common_indicator')}
            </label>
            <Col size="xs-12">
              {!indicatorId ? <ToggleSwitch
                disabled={!!indicatorId}
                defaultIsOn={indicator.is_common_indicator}
                name="is_common_indicator"
                handleToggle={(e) => updateIndicator('is_common_indicator', e.target.checked)}
              /> : hiddenCommonIndicatorInput}
              <small className="text-muted" style={{ fontWeight: "normal" }}>
                {I18n.t("common_indicator_description")}
              </small>
            </Col>
          </Row>
          <hr />
        </>
      ) : hiddenCommonIndicatorInput}

      {!indicatorId ? (
        <>
          <Row className="form-group">
            <label className="col-xs-12">
              {I18n.t("indicator_type")}
              <select
                name="subtype"
                className="form-control"
                value={subtype}
                onChange={(e) => updateIndicator("subtype", e.target.value)}
              >
                {Object.keys(INDICATOR_TYPE).filter(k => INDICATOR_TYPE[k] != INDICATOR_TYPE.aggregate || mayManageAggregateIndicators ).map((k) => (
                  <option key={k} value={INDICATOR_TYPE[k]}>
                    {I18n.t(k)}
                  </option>
                ))}
              </select>
              {isAggregate ? <small className="text-muted" style={{ fontWeight: "normal" }}>
                {I18n.t("indicator_type_description")}
              </small> : null}
            </label>
          </Row>
          {indicator.is_common_indicator ?
            <input type="hidden" name="parent_result_id" value="root" />
            : <Row className="form-group">
              <label className="col-xs-12">
                {I18n.t("parent_result")}
                <select
                  name="parent_result_id"
                  className="form-control"
                  value={resultId}
                  onChange={(e) => setResultId(e.target.value)}
                >
                  {(parentOptions).map((o, i) => (
                    <option key={i} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            </Row>
          }
        </>
      ) : (
        <input type="hidden" name="subtype" value={subtype} />
      )}
      <Row className="form-group">
        <ObjectiveNumberField objective={{ autoNumber, ...indicator }} />
      </Row>
      <Row className="form-group">
        <label className="col-xs-12">
          {I18n.t("title")} *
          <Help text={I18n.t("indicator_title_help")} />
          <textarea
            name="title"
            className="form-control"
            defaultValue={indicator.title}
            readOnly={lockCommonIndicatorFields}
          ></textarea>
        </label>
      </Row>
      <Row className="form-group">
        <label className="col-xs-12">
          {I18n.t("description")} *
          <Help text={I18n.t("indicator_description_help")} />
          <textarea
            name="description"
            className="form-control"
            defaultValue={indicator.description}
          ></textarea>
        </label>
      </Row>
      <Row className="form-group">
        <label className="col-xs-12">
          {I18n.t("sources_of_verification")} *
          <Help text={I18n.t("indicator_sources_of_verification_help")} />
          <textarea
            name="sources_of_verification"
            className="form-control"
            defaultValue={indicator.sources_of_verification}
          ></textarea>
        </label>
      </Row>
      {isQualitative ? (
        <QualitativeIndicatorFormFields
          indicator={indicator}
          milestonesEnabled={milestonesEnabled}
          formOptions={formOptions}
          resultFrameworkId={resultFrameworkId}
        />
      ) : (
        <QuantitativeIndicatorFormFields
          indicator={indicator}
          milestonesEnabled={milestonesEnabled}
          formOptions={formOptions}
          updateIndicator={updateIndicator}
          lockCommonIndicatorFields={lockCommonIndicatorFields}
        />
      )}
      <hr />
      <Row className="form-group">
        <label className="col-xs-12">
          {I18n.t("monitoring_interval")}
          <span className="more-info">{I18n.t("monitoring_months")}</span>
          <input
            type="text"
            name="interval"
            className="form-control"
            defaultValue={indicator.interval}
          ></input>
          <small className="text-muted" style={{ fontWeight: "normal" }}>
            {I18n.t("monitoring_interval_description")}
          </small>
        </label>
      </Row>
      <hr />
      {isQualitative || isAggregate ? null : (
        <>
          {hasLinks ? (
            <div className='alert alert-info'>
              {I18n.t('linked_indicator_disaggregations_info')}
            </div>
          ) : null}
          <Disaggregations 
            disaggregations={indicator.disaggregations} 
            readOnly={lockCommonIndicatorFields || hasLinks} 
            indicator={indicator}
            updateIndicator={updateIndicator}
            mayDefinePartialDisaggregations={mayDefinePartialDisaggregations}
          />
        </>
      )}
      {isAggregate ? null : <AdditionalFields
        additional_fields={indicator.additional_fields}
        data_types={formOptions.additional_field_data_types}
        readOnly={lockCommonIndicatorFields || hasLinks}
      />}
      {isAggregate ? <AggregateFormFields indicator={indicator} sourceIndicatorOptions={formOptions.source_indicator_options} /> : null}
    </form>
  );
};

export default IndicatorForm;
